import { useCallback } from 'react';

import useFluxibleAction from 'js/lib/fluxibleHooks/useFluxibleAction';
import useFluxibleStore from 'js/lib/fluxibleHooks/useFluxibleStore';

import { delink, link } from 'bundles/third-party-auth/actions/AccountLinkActions';
import type { ProviderName } from 'bundles/third-party-auth/components/AccountLink/types';
import {
  PROVIDER_LABELS,
  isOAuthDisabled,
} from 'bundles/third-party-auth/components/AccountLink/utils/linkedAccountStatus';
import { getErrorMessage, getSuccessMessage } from 'bundles/third-party-auth/components/AccountLink/utils/messages';
import AccountLinkStore from 'bundles/third-party-auth/stores/AccountLinkStore';

export type Provider = ProviderName;

type UseAccountLinkOptions = {
  grantSharePermissions?: boolean;
};

export const useAccountLink = (provider: Provider, options?: UseAccountLinkOptions) => {
  const { linked, loading, errorCode, successCode, currentAccountType } = useFluxibleStore(
    AccountLinkStore,
    (store) => ({
      linked: store.linked || {},
      loading: store.loading,
      errorCode: store.errorCode,
      successCode: store.successCode,
      currentAccountType: store.currentAccountType || '',
    })
  );
  const executeAction = useFluxibleAction();

  const isCurrentAccount = currentAccountType === provider;
  const isLoading = isCurrentAccount && loading;
  const isLinked = !!linked[provider];

  const handleLink = useCallback(() => {
    const payload: Record<string, unknown> = {
      accountType: provider,
      currentAccountType: provider,
    };
    if (options?.grantSharePermissions !== undefined) {
      payload.grantSharePermissions = options.grantSharePermissions;
    }
    executeAction(link, payload);
  }, [executeAction, provider, options?.grantSharePermissions]);

  const handleDelink = useCallback(
    (password?: string) => {
      const payload: { accountType: Provider; currentAccountType: Provider; password?: string } = {
        accountType: provider,
        currentAccountType: provider,
      };
      if (password !== undefined) {
        payload.password = password;
      }
      executeAction(delink, payload);
    },
    [executeAction, provider]
  );

  const isDisabled = isOAuthDisabled(provider, linked);
  const label = PROVIDER_LABELS[provider];
  const errorMessage = isCurrentAccount && errorCode ? getErrorMessage(errorCode, label, isLinked) : null;
  const successMessage = isCurrentAccount && successCode ? getSuccessMessage(successCode, label) : null;

  return {
    isCurrentAccount,
    isLoading,
    isLinked,
    isDisabled: isDisabled || isLoading,
    successCode,
    errorMessage,
    successMessage,
    handleLink,
    handleDelink,
  };
};

export default useAccountLink;
